/**
 * Parsers for the two gateway responses the provisioning screen reads.
 *
 * THE INPUT IS AN ALREADY-UNWRAPPED ARRAY, NOT `{ data: [...] }`.
 *
 * `request()` in `lib/api.ts` unwraps the gateway's `{ data: ... }` envelope
 * and hands back the inner value. An earlier version of these parsers read
 * `body.data` a second time, so every successful response parsed to an empty
 * list — which made the provisioning screen conclude nothing was being created
 * and leave immediately, on every single load. The screen never ran once.
 *
 * That is why these live here rather than inside the view: they are the seam
 * between an api-layer contract and a UI decision, and this project's vitest
 * environment is node-only, so anything left in a `.tsx` is covered by nothing.
 * The tests beside this file exercise the shape `request()` actually returns.
 *
 * Everything here treats the gateway as a trust boundary: values are coerced,
 * never asserted, and a shape that does not match yields a signal the caller
 * can act on rather than a plausible-looking empty result.
 */

import type { ReelWallet, ProvisionPlanEntry } from '@/lib/addressReel';

/** One UI/HTTP budget for the automatic wallet set; backend mirrors seven seconds. */
export const AUTO_PROVISION_TIMEOUT_MS = 7000;

/** Only an explicit request refusal establishes failure; transport outcomes do not. */
export function isProvisioningRequestRefused(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (('timeout' in error && error.timeout === true) ||
      ('code' in error && error.code === 'PROVISIONING_TIMEOUT')) return false;
  // No response, an unreadable response, and server/proxy failures can all
  // follow accepted creation. HTTP 408/409 can also leave work unresolved.
  return 'status' in error && typeof error.status === 'number' &&
    error.status >= 400 && error.status < 500 &&
    error.status !== 408 && error.status !== 409;
}

/** The three statuses the provision endpoint can report for a chain. */
const PLAN_STATUSES = ['existing', 'starting', 'unavailable'] as const;

/** Outcome of parsing a provision plan. */
export type PlanParse =
  | { ok: true; plan: ProvisionPlanEntry[] }
  | { ok: false };

/**
 * Parse the provision plan.
 *
 * Returns a FAILURE rather than an empty plan when the shape is wrong, because
 * the two mean opposite things to the caller: an empty plan says "nothing is
 * being created, you may leave", while a bad shape says "the client cannot
 * understand the server". Collapsing the second into the first is how the
 * screen came to exit silently on every load.
 *
 * `status` is validated against the known set rather than accepted as any
 * string. An unrecognised status would otherwise read as "not starting", which
 * is the same silent exit by another route.
 *
 * @param body The value returned by `request()` — the unwrapped array.
 * @returns The parsed plan, or a failure.
 */
export function parsePlan(body: unknown): PlanParse {
  if (!Array.isArray(body)) return { ok: false };
  const plan: ProvisionPlanEntry[] = [];
  for (const row of body) {
    const chain = (row as { chain?: unknown })?.chain;
    const status = (row as { status?: unknown })?.status;
    if (typeof chain !== 'string' || chain.length === 0) return { ok: false };
    if (typeof status !== 'string') return { ok: false };
    if (!(PLAN_STATUSES as readonly string[]).includes(status)) return { ok: false };
    plan.push({ chain, status });
  }
  return { ok: true, plan };
}

/**
 * Parse the wallet list into the shape the reel needs.
 *
 * Lenient where the plan parser is strict, and deliberately so: a malformed
 * plan means the screen cannot know what it is waiting for, while a malformed
 * wallet row only means that one row has not landed yet. Rows are coerced to
 * null rather than dropped so a partial record cannot masquerade as a wallet
 * with an address.
 *
 * @param body The value returned by `request()` — the unwrapped array.
 * @returns Chain and address pairs; empty when the shape is unusable.
 */
export function parseWallets(body: unknown): ReelWallet[] {
  if (!Array.isArray(body)) return [];
  return body.map((row) => {
    const chain = (row as { chain?: unknown })?.chain;
    const address = (row as { address?: unknown })?.address;
    return {
      chain: typeof chain === 'string' ? chain : null,
      // Trimmed, and only a real string: `strOrNull`-style coercion elsewhere
      // turns an object into "[object Object]", which is non-empty and would
      // read as a usable address.
      address: typeof address === 'string' && address.trim().length > 0 ? address : null,
    };
  });
}

/**
 * Whether a parsed plan has work the screen should wait for.
 *
 * A plan with nothing `starting` means this account already has its wallets, so
 * there is nothing to watch. That is what lets every authenticated entry point
 * route through the setup screen safely — a returning user passes straight
 * through instead of sitting through a fixed window for no reason.
 *
 * @param plan The parsed plan.
 * @returns True when at least one chain is being created.
 */
export function planHasWork(plan: ProvisionPlanEntry[]): boolean {
  return plan.some((p) => p.status === 'starting');
}

/** Which ending the provisioning screen reveals. */
export type ProvisioningEnding = 'done' | 'partial-failed' | 'failed';

/** The three things that can happen to the provisioning screen. */
export type ProvisioningEvent =
  /** The fixed window elapsed. */
  | 'window-closed'
  /** The provision request was explicitly refused. */
  | 'request-failed'
  /** The plan came back with nothing being created. */
  | 'no-work';

/** What the screen should do in response. */
export type ProvisioningAction =
  /** Keep going; the reel is still running and the window still owns the ending. */
  | { type: 'none' }
  /** Navigate to the dashboard immediately, without showing an ending. */
  | { type: 'leave' }
  /** Reveal (or correct) the ending on screen. */
  | { type: 'ending'; ending: ProvisioningEnding };

/**
 * Decide what the provisioning screen does next.
 *
 * WHY THIS IS A FUNCTION AND NOT THREE `if`s IN THE VIEW. The screen has one
 * fixed window and two results that can arrive on EITHER side of it, so the
 * behaviour is a product of two independent clocks. Every bug this file has seen
 * came from a late arrival:
 *
 *  - a POST that rejected at 3100ms marked five rows ✕ but left the committed
 *    ending saying the rest were still being created, because only the window
 *    ever set one;
 *  - a no-work plan that arrived after the window navigated away from an ending
 *    the user was already reading.
 *
 * As a total function over (event, state) those interleavings are enumerable and
 * tested; in the view they were three branches that each knew about only one
 * clock. This project's vitest environment is node-only, so logic left in a
 * `.tsx` is covered by nothing — see this file's header.
 *
 * THE COUNTS ARE ONLY CONSULTED AT THE CLOSE OF THE WINDOW, and they are what
 * separates the three endings. `created` is rows with a real address, `missing`
 * is everything else, and `pending` is the part of `missing` still in flight.
 *
 * PARTIAL MEANS `created` AND `missing` ARE BOTH NON-ZERO — some chains have
 * wallets and some do not — which is why a run that produced nothing is not a
 * partial naming zero successes.
 *
 * A closed window with no observed wallet uses the shared Failed presentation,
 * including timed-out requests and accepted plans with no observed addresses.
 * This UI choice does not establish or mutate backend creation state.
 *
 * @param event What just happened.
 * @param state windowClosed: the fixed window has already elapsed. failed: the
 *   provision call has failed at some point. created/missing/pending: how many
 *   chains ended the window with a wallet, without one, and still working.
 * @returns The action to take.
 */
export function provisioningAction(
  event: ProvisioningEvent,
  state: {
    windowClosed: boolean;
    failed: boolean;
    created: number;
    missing: number;
    pending: number;
  },
): ProvisioningAction {
  switch (event) {
    case 'window-closed':
      if (state.failed || state.created === 0) return { type: 'ending', ending: 'failed' };
      if (state.missing === 0) return { type: 'ending', ending: 'done' };
      return { type: 'ending', ending: 'partial-failed' };

    case 'request-failed':
      // Before the window closes there is nothing to say yet — the reel keeps
      // turning and the window will read the failure when it fires. After it,
      // an ending is already on screen and it is now wrong.
      return state.windowClosed ? { type: 'ending', ending: 'failed' } : { type: 'none' };

    case 'no-work':
      // Nothing to watch. Before the window, leave at once so a returning user
      // does not sit through three seconds of nothing on every sign-in. After
      // it, the user is reading an ending — correct it rather than navigating
      // out from under them.
      return state.windowClosed ? { type: 'ending', ending: 'done' } : { type: 'leave' };
  }
}
